import { ActionFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// Utils
import { compressSessionStorage } from '../../../../utils/sessionStorage';
import { claimDraftScope } from '../../../../utils/draftOwnership';
import {
  ACCIDENT_CREATION_DRAFT_KEYS,
  ACCIDENT_CREATION_SCOPE,
  ACCIDENT_MEDIA_DRAFT_KEYS,
  ACCIDENT_MEDIA_SCOPE,
  accidentCreationOwnerId,
} from '../../../../utils/draftScopes';

// Constants
import { formAccidentNaming } from '../../../../components/forms/AddBasicInfoAccidentForm/constants';

/**
 * La puerta que EMPIEZA un alta de accidente («Crear accidente» de la OT o de
 * la campaña, `ShowOT` → `pass-data`).
 *
 * 🔴 El borrador nuevo SUSTITUYE al anterior (spec 2026-11-02
 * wizard-draft-ownership, F3; auditoría de accidentes § 6, riesgos). Antes se
 * FUSIONABA `{...borrador anterior, orderParentId: nuevo}`: si quedaba el alta
 * de otra OT a medias, el accidente nuevo heredaba su descripción, su PK y sus
 * pasos 2 y 3. Hoy no se veía porque `ShowOT` vacía todo antes de llegar aquí;
 * la puerta deja de depender de eso.
 *
 * El dueño (`parent:<:id>`) se reclama AQUÍ, antes de escribir: los loaders
 * que corren después de esta acción reclaman el mismo id y, si el dueño fuera
 * todavía el del alta anterior, descartarían el borrador que se acaba de
 * escribir.
 */
export const AccidentNewAccident: ActionFunction = async ({ request, params }) => {
  const formData = await request.formData();
  const intent = formData.get('intent');
  if (intent === 'pass-data') {
    try {
      // El padre es el de la URL (I3); el `id` del formulario es el mismo
      // (`ShowOT` manda el de la OT que pinta) y queda de reserva.
      const formParentId = formData.get('id');
      const orderParentId =
        params.id ?? (typeof formParentId === 'string' ? formParentId : undefined);
      const orderParentRoid = formData.get('roid');

      const owner = accidentCreationOwnerId(orderParentId);
      claimDraftScope(ACCIDENT_CREATION_SCOPE, owner);
      claimDraftScope(ACCIDENT_MEDIA_SCOPE, owner);
      // Alta NUEVA: aunque el dueño ya fuera este padre (un alta suya a
      // medias), se empieza de cero.
      [...ACCIDENT_CREATION_DRAFT_KEYS, ...ACCIDENT_MEDIA_DRAFT_KEYS].forEach((key) =>
        sessionStorage.removeItem(key)
      );

      sessionStorage.setItem(
        formAccidentNaming.NEW_ACCIDENT_FORM,
        compressSessionStorage(JSON.stringify({ orderParentId, orderParentRoid }))
      );
      return { orderParentId, orderParentRoid };
    } catch (err) {
      Sentry.captureException(err);
      console.log('> NewAccident pass-data - error creating:', err);
    }
  }

  return null;
};
